const API_BASE_URL = 'https://tmis.co.za/TMIS_POS_FE/Portal_Api/api';

export class ApiService {

  private parseStoredToken(tokenString: string | null): string | null {
    if (!tokenString) return null;
    try {
      const parsed = JSON.parse(tokenString);
      if (parsed && typeof parsed === 'object') return parsed.value ?? String(parsed);
      return String(parsed);
    } catch {
      return tokenString;
    }
  }

  private getAuthHeaders(): HeadersInit {
    const raw = localStorage.getItem('pos_access_token');
    const token = this.parseStoredToken(raw);
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` })
    };
  }

  // ------------------------------------
  // 🔥 GLOBAL TOKEN EXPIRED HANDLER
  // ------------------------------------
  private async handleUnauthorized(response: Response) {
    if (response.status !== 401) return;

    let message = "";
    try {
      const data = await response.clone().json();
      message = data?.error_description || data?.Messages?.[0] || "";
    } catch {
      // ignore parsing errors
    }

    if (
      message.toLowerCase().includes("expired") ||
      message.toLowerCase().includes("invalid_token") ||
      response.status === 401
    ) {
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = "/login";
      return;
    }
  }

  // --- Existing methods ---

  async getCheckInBookings() {
    const response = await fetch(`${API_BASE_URL}/CheckIn/list/bookings`, {
      method: 'GET',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async getCheckInBookingGuests(bookingId: number, accountId?: number | null) {
    const payload = { BookingID: bookingId, FK_AccountID: accountId ?? null };
    const response = await fetch(`${API_BASE_URL}/CheckIn/list/guests/linked/unlinked`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async getAccountGuests(accountId: string) {
    const payload = { AccountID: accountId };
    const response = await fetch(`${API_BASE_URL}/CheckIn/list/account/guests`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async getAccounts() {
    const response = await fetch(`${API_BASE_URL}/CheckIn/list/account`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async getBookingAccounts(bookingHeaderId: number) {
    const response = await fetch(`${API_BASE_URL}/CheckIn/list/booking/account`, {
      method: 'POST',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
        'accept': '*/*',
      },
      body: JSON.stringify({ FK_BookingID: bookingHeaderId }),
    });

    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async addAccount(payload: { Name: string; FK_BookingID: number; FK_ResponsibleID: number }) {
    const response = await fetch(`${API_BASE_URL}/CheckIn/add/account`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async staffLogin(staffPin: number) {
    const response = await fetch(`${API_BASE_URL}/Management/staff/login`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ StaffPin: staffPin }),
    });
    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async addAccountGuest(
    accountId: number,
    staffId: number,
    guests: { GuestID: number; IsResponsible: boolean }[]
  ) {
    const payload = {
      FK_AccountID: accountId,
      FK_StaffID: staffId,
      Guests: guests,
    };

    const response = await fetch(`${API_BASE_URL}/CheckIn/add/account/guest`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async closeAccount(accountId: number | string) {
    const response = await fetch(`${API_BASE_URL}/CheckIn/close/account`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ AccountID: accountId }),
    });

    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  async removeAccountGuest(accountGuestId: number) {
    const payload = { AccountGuestID: accountGuestId };
    const response = await fetch(`${API_BASE_URL}/CheckIn/remove/account/guest`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async makeAuthenticatedRequest(endpoint: string, options: RequestInit = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: { ...this.getAuthHeaders(), ...options.headers },
    });
    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async fullCheckout(accountId: string, staffId: number, paymentTypeId: number) {
    const response = await fetch(`${API_BASE_URL}/CheckIn/full/checkout`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        AccountID: accountId,
        StaffID: staffId,
        PaymentTypeID: paymentTypeId,
      }),
    });

    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async partialCheckout(accountId: string, isSettled: boolean, guestIds: number[], staffID: number) {
    const response = await fetch(`${API_BASE_URL}/CheckIn/partial/checkout`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        AccountID: accountId,
        IsSettled: isSettled,
        Guests: guestIds.map((id) => ({ GuestID: id })),
        StaffID: staffID,
      }),
    });

    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async listPaymentTypes() {
    const response = await fetch(`${API_BASE_URL}/Sales/list/payment/types`, {
      headers: this.getAuthHeaders(),
    });
    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async generateAccountInvoice(accountId: string) {
    const response = await fetch(`${API_BASE_URL}/CheckIn/generate/account/invoice`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ AccountID: accountId }),
    });

    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async getAccountInvoices(accountId: string) {
    const response = await fetch(`${API_BASE_URL}/CheckIn/get/account/invoices`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ AccountID: accountId }),
    });

    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async listMainGuestAccounts(guestId: number) {
    const response = await fetch(`${API_BASE_URL}/CheckIn/list/main/guest/accounts`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ GuestID: guestId }),
    });

    if (!response.ok) {
      await this.handleUnauthorized(response);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }
}

export const apiService = new ApiService();
